import { faSort, faSortDown, faSortUp } from '@fortawesome/pro-solid-svg-icons';
import type { HTMLAttributes } from 'react';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { usePagination } from '../../hooks/usePagination';
import { useListBuilderContextSafe } from '../../list-builder/components/core/ListBuilderProvider';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useScrollContext } from '../../services/ScrollContext';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';
import { Checkbox } from '../forms/Checkbox';
import { LoadingSpinner } from './LoadingSpinner';
import { TablePaginationControls } from './TablePaginationControls';

// Auto-sizing table components
export interface TableColumn {
	key: string;
	label: string | React.ReactNode;
	align?: 'left' | 'center' | 'right';
	width?: number; // Fixed width (acts as both minWidth and maxWidth)
	minWidth?: number;
	maxWidth?: number;
	sortable?: boolean;
	sticky?: boolean; // Make this column sticky to the left when scrolling horizontally
	render?: (value: any, item: any, index: number) => React.ReactNode;
	// Per-row column span. Return N > 1 from this column to emit a single
	// <td colSpan={N}> and absorb the next N-1 columns' cells for this row.
	// Useful for drilldown/sub-header rows that don't map 1:1 to the
	// parent-row columns.
	colSpan?: (value: any, item: any, index: number) => number;
}

interface MeasuredSizes {
	[columnKey: string]: number;
}

export interface TableProps<T = any>
	extends Omit<HTMLAttributes<HTMLDivElement>, 'onScroll'> {
	columns: TableColumn[];
	data: T[];
	onSort?: (key: string, direction: 'asc' | 'desc') => void;
	height?: number;
	maxHeight?: number;
	stickyHeader?: boolean;
	striped?: boolean;
	compact?: boolean;
	getRowClassName?: (row: T, index: number) => string;
	getRowExpansion?: (row: T, index: number) => React.ReactNode | null;
	onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
	onRowClick?: (row: T, index: number) => void;
	selectedRows?: Set<string | number>;
	onSelectRow?: (rowId: string | number, selected: boolean) => void;
	onSelectAll?: (selected: boolean) => void;
	showCheckboxes?: boolean;
	loading?: boolean;
	emptyMessage?: string;
	/** Enable client-side pagination. Table slices data internally. */
	pagination?: {
		pageSize?: number; // default: 100
	};
	/** Enable built-in client-side sorting. When true, clicking sortable column headers
	 *  sorts data internally. When false/omitted, sorting is handled externally via onSort callback. */
	clientSort?: boolean;
}

// Helper function to get scrollbar width (keep for width calculations)
const getScrollbarWidth = () => {
	if (typeof window === 'undefined') {
		return 0;
	}

	const outer = document.createElement('div');
	outer.style.visibility = 'hidden';
	outer.style.overflow = 'scroll';
	outer.style.width = '100px';
	outer.style.position = 'absolute';
	outer.style.top = '-9999px';
	document.body.appendChild(outer);

	const inner = document.createElement('div');
	inner.style.width = '100%';
	outer.appendChild(inner);

	const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
	outer.parentNode?.removeChild(outer);

	return scrollbarWidth;
};

export function Table<T = any>({
	columns,
	data,
	onSort,
	className = '',
	height,
	maxHeight,
	stickyHeader = true,
	striped = true,
	compact = false,
	getRowClassName,
	getRowExpansion,
	onScroll,
	onRowClick,
	selectedRows,
	onSelectRow,
	onSelectAll,
	showCheckboxes,
	loading = false,
	emptyMessage = 'No data available',
	pagination,
	clientSort = false,
	style,
	...props
}: TableProps<T>) {
	const colors = useThemeColors();
	const listKey = useListBuilderContextSafe()?.listKey;

	const [columnWidths, setColumnWidths] = useState<MeasuredSizes>({});
	const [containerWidth, setContainerWidth] = useState<number>(0); // Start with 0 to prevent premature calculations
	const [hasInitializedWidths, setHasInitializedWidths] = useState(false);
	const [isResizing, setIsResizing] = useState<string | null>(null);
	const [manuallyResizedColumns, setManuallyResizedColumns] = useState<
		Set<string>
	>(new Set());
	const [hoveredRow, setHoveredRow] = useState<number | null>(null);

	// Memoize scrollbar width to avoid recalculating
	const scrollbarWidth = useMemo(() => {
		if (height && data.length > 0) {
			return getScrollbarWidth();
		}
		return 0;
	}, [height, data.length]);

	// Client-side sort state (only used when clientSort is enabled)
	const [internalSortKey, setInternalSortKey] = useState<string | null>(null);
	const [internalSortDirection, setInternalSortDirection] = useState<
		'asc' | 'desc'
	>('asc');

	// Generic comparator for client-side sorting
	const sortedData = useMemo(() => {
		if (!clientSort || internalSortKey == null) {
			return data;
		}

		return [...data].sort((a, b) => {
			const aVal = (a as Record<string, unknown>)[internalSortKey];
			const bVal = (b as Record<string, unknown>)[internalSortKey];

			// Nulls go to the end regardless of direction
			if (aVal == null && bVal == null) return 0;
			if (aVal == null) return 1;
			if (bVal == null) return -1;

			let comparison = 0;
			if (typeof aVal === 'number' && typeof bVal === 'number') {
				comparison = aVal - bVal;
			} else if (typeof aVal === 'string' && typeof bVal === 'string') {
				comparison = aVal.localeCompare(bVal, undefined, {
					sensitivity: 'base',
				});
			} else {
				// Fallback: convert to string for comparison
				comparison = String(aVal).localeCompare(String(bVal), undefined, {
					sensitivity: 'base',
				});
			}

			return internalSortDirection === 'asc' ? comparison : -comparison;
		});
	}, [data, clientSort, internalSortKey, internalSortDirection]);

	// Pagination: slice the (optionally sorted) data
	const paginationEnabled = pagination != null;
	const pageSize = pagination?.pageSize ?? 100;

	const {
		currentPage,
		totalPages,
		paginatedData,
		goToPage,
		startIndex: pageStartIndex,
		endIndex: pageEndIndex,
		totalItems,
	} = usePagination({
		data: sortedData,
		itemsPerPage: pageSize,
	});

	// The data to actually render in the table body
	const renderData = paginationEnabled ? paginatedData : sortedData;

	// Handle column header click for sorting
	const handleSortClick = useCallback(
		(key: string) => {
			if (clientSort) {
				setInternalSortDirection((prevDir) => {
					if (internalSortKey === key) {
						return prevDir === 'asc' ? 'desc' : 'asc';
					}
					return 'desc';
				});
				setInternalSortKey(key);
			}

			// Also call external onSort if provided (for analytics/tracking)
			if (onSort != null) {
				const newDirection =
					internalSortKey === key
						? internalSortDirection === 'asc'
							? 'desc'
							: 'asc'
						: 'desc';
				onSort(key, newDirection);
			}
		},
		[clientSort, internalSortKey, internalSortDirection, onSort],
	);

	// Calculate sticky column positions
	const stickyColumnPositions = useMemo(() => {
		const positions: { [key: string]: number } = {};
		let leftOffset = 0;

		columns.forEach((column) => {
			if (column.sticky) {
				positions[column.key] = leftOffset;
				const width =
					columnWidths[column.key] || column.width || column.minWidth || 120;
				leftOffset += width;
			}
		});

		return positions;
	}, [columns, columnWidths]);

	const containerRef = useRef<HTMLDivElement>(null);
	const tableRef = useRef<HTMLTableElement>(null);

	// Calculate column widths based on container dimensions and constraints
	const calculateColumnWidths = useCallback(() => {
		setColumnWidths((prevWidths) => {
			const widths: MeasuredSizes = { ...prevWidths };

			// If any column has been manually resized, stop trying to fit table to container width
			const hasManualResizes = manuallyResizedColumns.size > 0;

			// Step 1: Identify column types and calculate initial requirements
			const fixedColumns: TableColumn[] = [];
			const flexibleColumns: TableColumn[] = [];
			const manuallyResizedList: TableColumn[] = [];
			let totalFixedWidth = 0;
			let totalManualWidth = 0;

			columns.forEach((col) => {
				if (manuallyResizedColumns.has(col.key)) {
					// Manually resized columns - preserve their current width
					manuallyResizedList.push(col);
					totalManualWidth +=
						widths[col.key] || col.width || col.minWidth || 120;
				} else if (col.width) {
					// Fixed width columns
					fixedColumns.push(col);
					widths[col.key] = col.width;
					totalFixedWidth += col.width;
				} else {
					// Flexible columns (not manually resized)
					flexibleColumns.push(col);
					if (!widths[col.key]) {
						widths[col.key] = col.minWidth || 120; // Default starting width
					}
				}
			});

			if (hasManualResizes) {
				// User has manually resized columns - don't constrain to container width
				flexibleColumns.forEach((col) => {
					if (!widths[col.key]) {
						widths[col.key] = col.minWidth || 120;
					}
				});
			} else {
				// No manual resizes - try to fit table to container width
				const spacingWidth = Math.max(0, (columns.length - 1) * 8);
				const cellPaddingTotal = columns.length * 24; // px-3 = 12px per side
				const borderWidth = 2;
				const buffer = 2;
				const availableWidth = Math.max(
					100,
					containerWidth -
						spacingWidth -
						cellPaddingTotal -
						borderWidth -
						scrollbarWidth -
						buffer,
				);
				const reservedWidth = totalFixedWidth + totalManualWidth;
				const remainingWidth = Math.max(0, availableWidth - reservedWidth);

				if (flexibleColumns.length > 0) {
					if (remainingWidth > 0) {
						// Distribute available width among flexible columns
						const totalFlexibleMinWidth = flexibleColumns.reduce(
							(sum, col) => sum + (col.minWidth || 120),
							0,
						);

						if (remainingWidth >= totalFlexibleMinWidth) {
							// We have enough space - distribute proportionally with precise rounding
							const extraSpace = remainingWidth - totalFlexibleMinWidth;
							let distributedSpace = 0;

							flexibleColumns.forEach((col, index) => {
								const baseWidth = col.minWidth || 120;

								// Calculate space for this column, ensuring we don't exceed total
								const isLastColumn = index === flexibleColumns.length - 1;
								const spaceForThisColumn = isLastColumn
									? extraSpace - distributedSpace // Give remaining space to last column
									: Math.floor(extraSpace / flexibleColumns.length);

								distributedSpace += spaceForThisColumn;
								let finalWidth = baseWidth + spaceForThisColumn;

								// Respect maxWidth constraint
								if (col.maxWidth) {
									finalWidth = Math.min(finalWidth, col.maxWidth);
								}

								widths[col.key] = Math.round(finalWidth);
							});
						} else {
							// Not enough space - shrink below default but respect explicit minWidth
							let distributedWidth = 0;
							const absoluteMinWidth = 40; // Absolute minimum to keep columns usable

							flexibleColumns.forEach((col, index) => {
								const isLastColumn = index === flexibleColumns.length - 1;

								if (isLastColumn) {
									// Give remaining width to last column
									const targetWidth = remainingWidth - distributedWidth;
									const effectiveMin = col.minWidth || absoluteMinWidth;
									widths[col.key] = Math.max(
										effectiveMin,
										Math.round(targetWidth),
									);
								} else {
									// Distribute space equally among flexible columns
									const targetWidth = remainingWidth / flexibleColumns.length;
									const effectiveMin = col.minWidth || absoluteMinWidth;
									const roundedWidth = Math.max(
										effectiveMin,
										Math.round(targetWidth),
									);
									widths[col.key] = roundedWidth;
									distributedWidth += roundedWidth;
								}
							});
						}
					} else {
						// No space left for flexible columns - respect explicit minWidth or use small default
						flexibleColumns.forEach((col) => {
							const effectiveMin = col.minWidth || 40;
							widths[col.key] = effectiveMin;
						});
					}

					// Final precision check - only reduce if we can respect minWidth constraints
					const totalCalculatedWidth = Object.values(widths).reduce(
						(sum, w) => sum + w,
						0,
					);
					const actualOverflow = totalCalculatedWidth - availableWidth;

					if (actualOverflow > 1 && flexibleColumns.length > 0) {
						// Allow 1px tolerance
						// Check if we can reduce overflow while respecting explicit minWidth
						const totalReducibleSpace = flexibleColumns.reduce((sum, col) => {
							const currentWidth = widths[col.key] || 0;
							const effectiveMin = col.minWidth || 40; // Only respect explicit minWidth
							return sum + Math.max(0, currentWidth - effectiveMin);
						}, 0);

						if (totalReducibleSpace >= actualOverflow) {
							// We can reduce the overflow while respecting constraints
							let remainingOverflow = actualOverflow;

							flexibleColumns.forEach((col, index) => {
								if (remainingOverflow <= 0) {
									return;
								}

								const isLastColumn = index === flexibleColumns.length - 1;
								const currentWidth = widths[col.key] || 0;
								const effectiveMin = col.minWidth || 40;
								const maxReduction = currentWidth - effectiveMin;

								if (maxReduction > 0) {
									if (isLastColumn) {
										// Last column takes all remaining overflow (up to its limit)
										const reductionAmount = Math.min(
											remainingOverflow,
											maxReduction,
										);
										widths[col.key] = Math.round(
											currentWidth - reductionAmount,
										);
									} else {
										// Reduce proportionally
										const reductionAmount = Math.min(
											Math.ceil(
												remainingOverflow / (flexibleColumns.length - index),
											),
											maxReduction,
										);

										if (reductionAmount > 0) {
											widths[col.key] = Math.round(
												currentWidth - reductionAmount,
											);
											remainingOverflow -= reductionAmount;
										}
									}
								}
							});
						}
					}
				}
			}

			return widths;
		});
	}, [columns, manuallyResizedColumns, containerWidth, scrollbarWidth]);

	// Use ResizeObserver for container width changes (better than onLayout)
	useEffect(() => {
		if (!containerRef.current) {
			return;
		}

		const resizeObserver = new ResizeObserver((entries) => {
			const { width, height: observedHeight } = entries[0].contentRect;

			if (width > 0 && Math.abs(width - containerWidth) > 1) {
				// Only update if significant change
				setContainerWidth(width);
			}
		});

		resizeObserver.observe(containerRef.current);
		return () => {
			resizeObserver.disconnect();
		};
	}, [containerWidth]);

	// Determine if we should show content or wait for measurement
	const shouldShowContent = containerWidth > 0;

	// Calculate column widths on initial load and container width changes
	// Only calculate when we're actually going to show content
	useEffect(() => {
		if (shouldShowContent) {
			// Only recalculate if not currently resizing
			if (!isResizing) {
				calculateColumnWidths();
				setHasInitializedWidths(true);
			}
		}
	}, [
		shouldShowContent,
		containerWidth,
		columns,
		isResizing,
		scrollbarWidth,
		calculateColumnWidths,
	]);

	// Reset manual resize tracking when columns change
	useEffect(() => {
		if (hasInitializedWidths && shouldShowContent) {
			// Check if columns have changed significantly (different keys)
			const currentKeys = new Set(columns.map((col) => col.key));
			const existingKeys = new Set(Object.keys(columnWidths));

			const hasNewColumns = columns.some((col) => !existingKeys.has(col.key));
			const hasRemovedColumns = [...existingKeys].some(
				(key) => !currentKeys.has(key),
			);

			if (hasNewColumns || hasRemovedColumns) {
				// Reset manual resize tracking for removed columns
				setManuallyResizedColumns((prev) => {
					const newSet = new Set<string>();
					for (const key of prev) {
						if (currentKeys.has(key)) {
							newSet.add(key);
						}
					}
					return newSet;
				});

				// Add widths for new columns
				const newWidths = { ...columnWidths };
				columns.forEach((col) => {
					if (!newWidths[col.key]) {
						newWidths[col.key] = col.width || col.minWidth || 120;
					}
				});

				// Remove widths for removed columns
				Object.keys(newWidths).forEach((key) => {
					if (!currentKeys.has(key)) {
						delete newWidths[key];
					}
				});

				setColumnWidths(newWidths);
			}
		}
	}, [columns, hasInitializedWidths, columnWidths]);

	// Render content for a cell
	const renderCellContent = (column: TableColumn, item: T, index: number) => {
		const value = item[column.key as keyof T];

		if (column.render) {
			return column.render(value, item, index);
		}

		if (typeof value === 'string' || typeof value === 'number') {
			return <span className="block truncate">{value}</span>;
		}

		return null;
	};

	const getAlignmentClass = (align?: 'left' | 'center' | 'right') => {
		switch (align) {
			case 'center':
				return 'text-center';
			case 'right':
				return 'text-right';
			default:
				return 'text-left';
		}
	};

	// Enhanced column resizing with native mouse events
	const handleMouseDown = (e: React.MouseEvent, columnKey: string) => {
		e.preventDefault();
		e.stopPropagation();

		const startX = e.clientX;
		const startWidth = columnWidths[columnKey] || 120;
		setIsResizing(columnKey);

		const handleMouseMove = (e: MouseEvent) => {
			const deltaX = e.clientX - startX;
			const newWidth = startWidth + deltaX;

			// Find the column configuration
			const column = columns.find((col) => col.key === columnKey);
			if (!column) {
				return;
			}

			// Apply constraints using user-defined minimum
			const effectiveMinWidth = column.width || column.minWidth || 60;
			const maxWidth = column.width || column.maxWidth;

			let constrainedWidth = Math.max(newWidth, effectiveMinWidth);
			if (maxWidth) {
				constrainedWidth = Math.min(constrainedWidth, maxWidth);
			}

			setColumnWidths((prev) => ({
				...prev,
				[columnKey]: constrainedWidth,
			}));
		};

		const handleMouseUp = () => {
			setIsResizing(null);
			setManuallyResizedColumns((prev) => new Set(prev).add(columnKey));
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	};

	// Scroll context integration (simplified)
	const scrollContext = useScrollContext();
	const [scrollY, setScrollY] = useState(0);

	const handleScroll = useCallback(
		(event: React.UIEvent<HTMLDivElement>) => {
			const currentScrollY = event.currentTarget.scrollTop;
			setScrollY(currentScrollY);

			// Notify scroll context if available
			if (scrollContext) {
				// Note: This is a simplified integration - full context integration may need adjustment
			}

			// Call original onScroll if provided
			if (onScroll) {
				onScroll(event);
			}
		},
		[onScroll, scrollContext],
	);

	// Render sort indicator icon for clientSort mode
	const renderSortIcon = (columnKey: string) => {
		if (!clientSort || !columns.find((c) => c.key === columnKey)?.sortable) {
			return null;
		}

		const isActive = internalSortKey === columnKey;
		const icon = isActive
			? internalSortDirection === 'asc'
				? faSortUp
				: faSortDown
			: faSort;

		return (
			<Icon
				icon={icon}
				size={12}
				color={isActive ? colors.values.primary : colors.values.iconSecondary}
				style={{ marginLeft: 4, flexShrink: 0 }}
			/>
		);
	};

	// Render header cell with resizing handle
	const renderHeaderCell = (column: TableColumn, index: number) => {
		const width = columnWidths[column.key] || column.minWidth || 120;
		const isLastColumn = index === columns.length - 1;
		const isSticky = column.sticky;
		const stickyLeft = isSticky ? stickyColumnPositions[column.key] : undefined;

		// Determine the sort handler: use internal handleSortClick when clientSort is on,
		// otherwise fall back to external onSort
		const sortHandler = clientSort
			? handleSortClick
			: onSort != null
				? (key: string) => {
						onSort(key, 'asc');
					}
				: undefined;

		return (
			<th
				key={column.key}
				className={cn(
					colors.classes.tableHeader,
					colors.classes.tableHeaderCell,
					colors.classes.borderDefault,
					'border-b px-3 py-2 text-xs relative select-none',
					getAlignmentClass(column.align),
					isSticky && 'sticky z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]',
				)}
				style={{
					width,
					minWidth: width,
					...(isSticky && { left: stickyLeft }),
				}}
				data-testid={listKey != null ? `${listKey}-column-header` : undefined}
			>
				{React.isValidElement(column.label) ? (
					column.sortable ? (
						<button
							onClick={() => {
								sortHandler?.(column.key);
							}}
							className={cn(
								'w-full h-full flex items-center whitespace-nowrap',
								column.align === 'center' && 'justify-center',
								column.align === 'right' && 'justify-end',
								column.align === 'left' && 'justify-start',
								!column.align && 'justify-start',
								'hover:opacity-80 cursor-pointer',
								getAlignmentClass(column.align),
							)}
						>
							{column.label}
							{renderSortIcon(column.key)}
						</button>
					) : (
						<div
							className={cn(
								'w-full h-full flex items-center whitespace-nowrap',
								column.align === 'center' && 'justify-center',
								column.align === 'right' && 'justify-end',
								column.align === 'left' && 'justify-start',
								!column.align && 'justify-start',
							)}
						>
							{column.label}
						</div>
					)
				) : (
					<button
						onClick={() => {
							if (column.sortable) {
								sortHandler?.(column.key);
							}
						}}
						disabled={!column.sortable}
						className={cn(
							'w-full h-full flex items-center',
							getAlignmentClass(column.align),
							column.align === 'center' && 'justify-center',
							column.align === 'right' && 'justify-end',
							column.sortable && 'hover:opacity-80 cursor-pointer',
						)}
					>
						<span className="block truncate">{column.label}</span>
						{renderSortIcon(column.key)}
					</button>
				)}

				{/* Column resize handle */}
				{!isLastColumn && (
					<div
						className={cn(
							'absolute top-0 right-0 w-2 h-full cursor-col-resize bg-transparent opacity-0 hover:opacity-100 transition-opacity',
							`hover:${colors.classes.bgPrimary}/20`,
						)}
						onMouseDown={(e) => {
							handleMouseDown(e, column.key);
						}}
					>
						<div
							className={cn(
								'w-px h-full mx-auto',
								colors.classes.borderDefault,
							)}
						/>
					</div>
				)}
			</th>
		);
	};

	// Render table row with expansion support
	const renderRow = (item: T, index: number) => {
		const expansion = getRowExpansion?.(item, index);
		const rowClassName = getRowClassName?.(item, index) || '';

		// Build base classes - don't apply stripe if there's a custom background
		const hasCustomBackground = rowClassName.includes('bg-');
		const isHovered = onRowClick != null && hoveredRow === index;
		const baseClasses = cn(
			index < renderData.length - 1 &&
				!expansion &&
				cn('border-b', colors.classes.borderDefault),
			// Hover color is intentionally darker than the stripe color so the
			// hover state is visible on both striped and unstriped rows. We
			// also suppress the stripe class on the hovered row below because
			// the stripe's :nth-child(even) selector is more specific than a
			// plain bg utility and would otherwise win the cascade.
			isHovered && 'bg-gray-200 dark:bg-gray-600',
		);

		return (
			<React.Fragment key={(item as any)._id || (item as any).id || index}>
				{/* Row expansion - now appears above the row */}
				{expansion && (
					<tr className={cn(rowClassName, 'border-b-0')}>
						<td
							colSpan={columns.length + (showCheckboxes === true ? 1 : 0)}
							className="px-3 pb-0 pt-2"
						>
							<div
								className={cn(
									'rounded-lg p-4 mb-2',
									rowClassName.includes('bg-red')
										? 'bg-red-200/50 dark:bg-red-900/30'
										: rowClassName.includes('bg-yellow')
											? 'bg-yellow-200/50 dark:bg-yellow-900/30'
											: 'bg-gray-200/50 dark:bg-gray-800/50',
								)}
							>
								{expansion}
							</div>
						</td>
					</tr>
				)}

				<tr
					className={cn(
						baseClasses,
						rowClassName,
						'transition-colors duration-150',
						striped &&
							!hasCustomBackground &&
							!isHovered &&
							colors.classes.tableRowStripe,
					)}
					onMouseEnter={() => onRowClick && setHoveredRow(index)}
					onMouseLeave={() => onRowClick && setHoveredRow(null)}
					onClick={(e) => {
						if (onRowClick == null) return;
						// Don't fire row click when clicking interactive elements (buttons, links, inputs)
						const target = e.target as HTMLElement;
						if (
							target.closest('button, a, input, select, [role="button"]') !=
							null
						)
							return;
						onRowClick(item, index);
					}}
					style={{ cursor: onRowClick ? 'pointer' : 'default' }}
					data-testid={listKey != null ? `${listKey}-list-row` : undefined}
				>
					{showCheckboxes === true &&
						(() => {
							// Prefer `id`, but fall back to `_id` for entities served straight
							// from TypeORM raw results (e.g. ItemsService). Matches the
							// extraction used by ListView's handleSelectAll so row selection
							// stays consistent regardless of which field the source exposes.
							const rowRecord = item as Record<string, unknown>;
							const rowId = (rowRecord.id ?? rowRecord._id) as string | number;
							return (
								<td
									className={cn(
										'py-2 px-2 text-center',
										colors.classes.tableCell,
									)}
									style={{ width: 40, minWidth: 40 }}
									onClick={(e) => {
										e.stopPropagation();
									}}
								>
									<Checkbox
										checked={selectedRows != null && selectedRows.has(rowId)}
										onChange={(checked) => onSelectRow?.(rowId, checked)}
									/>
								</td>
							);
						})()}
					{(() => {
						// Track how many subsequent columns have been absorbed by a
						// prior column's colSpan so we can skip rendering their <td>.
						let skipRemaining = 0;
						return columns.map((column, colIndex) => {
							if (skipRemaining > 0) {
								skipRemaining -= 1;
								return null;
							}
							const value = (item as Record<string, unknown>)[column.key];
							const span = Math.max(
								1,
								Math.min(
									column.colSpan?.(value, item, index) ?? 1,
									columns.length - colIndex,
								),
							);
							if (span > 1) {
								skipRemaining = span - 1;
							}
							const width = columnWidths[column.key] || column.minWidth || 120;
							const isSticky = column.sticky;
							const stickyLeft = isSticky
								? stickyColumnPositions[column.key]
								: undefined;

							return (
								<td
									key={column.key}
									colSpan={span > 1 ? span : undefined}
									className={cn(
										'py-2 px-3 overflow-hidden',
										colors.classes.tableCell,
										getAlignmentClass(column.align),
										isSticky &&
											cn(
												'sticky z-10',
												colors.classes.bgPrimary,
												'shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]',
											),
									)}
									style={{
										width: span > 1 ? undefined : width,
										minWidth: span > 1 ? undefined : width,
										...(isSticky && { left: stickyLeft }),
									}}
								>
									{renderCellContent(column, item, index)}
								</td>
							);
						});
					})()}
				</tr>
			</React.Fragment>
		);
	};

	// The table element, shared between paginated and non-paginated layouts
	const tableElement = shouldShowContent ? (
		<table
			ref={tableRef}
			className="border-collapse table-fixed text-xs min-w-full"
			data-testid={listKey != null ? `${listKey}-list-table` : undefined}
		>
			<thead className={stickyHeader ? 'sticky top-0 z-10' : ''}>
				<tr style={{ minHeight: '36px' }}>
					{showCheckboxes === true && (
						<th
							className={cn(
								colors.classes.tableHeader,
								colors.classes.tableHeaderCell,
								colors.classes.borderDefault,
								'border-b px-2 py-2 text-center',
							)}
							style={{ width: 40, minWidth: 40 }}
						>
							<Checkbox
								checked={
									data.length > 0 &&
									selectedRows != null &&
									selectedRows.size > 0 &&
									data.every((row) => {
										const rowRecord = row as Record<string, unknown>;
										const rowId = (rowRecord.id ?? rowRecord._id) as
											| string
											| number;
										return selectedRows.has(rowId);
									})
								}
								indeterminate={
									selectedRows != null &&
									selectedRows.size > 0 &&
									!data.every((row) => {
										const rowId = (row as Record<string, unknown>).id as
											| string
											| number;
										return selectedRows.has(rowId);
									})
								}
								onChange={(checked) => onSelectAll?.(checked)}
							/>
						</th>
					)}
					{columns.map(renderHeaderCell)}
				</tr>
			</thead>
			<tbody>
				{loading ? (
					<tr>
						<td
							colSpan={columns.length + (showCheckboxes === true ? 1 : 0)}
							className="text-center py-8"
						>
							<div
								className="inline-flex items-center"
								data-testid={
									listKey != null ? `${listKey}-table-loading` : undefined
								}
							>
								<LoadingSpinner size="sm" color="gray" className="mr-3" />
								<span className={colors.classes.textSecondary}>Loading...</span>
							</div>
						</td>
					</tr>
				) : data.length === 0 ? (
					<tr>
						<td
							colSpan={columns.length + (showCheckboxes === true ? 1 : 0)}
							className={cn('text-center py-12', colors.classes.textSecondary)}
							data-testid={
								listKey != null ? `${listKey}-table-empty` : undefined
							}
						>
							<div className="flex items-center justify-center">
								<span className="text-sm">{emptyMessage}</span>
							</div>
						</td>
					</tr>
				) : (
					renderData.map(renderRow)
				)}
			</tbody>
		</table>
	) : (
		<div className="h-full w-full" style={{ overflow: 'hidden' }} />
	);

	// When pagination is enabled, wrap in a flex layout with controls pinned at bottom
	if (paginationEnabled) {
		return (
			<div
				className={cn(
					'border rounded-t-lg h-full flex flex-col',
					colors.classes.borderDefault,
					colors.classes.bgPrimary,
					className,
				)}
				style={(() => {
					const computedHeight = height ? height : undefined;
					const computedMaxHeight = shouldShowContent
						? !height && maxHeight
							? maxHeight
							: undefined
						: undefined;

					return {
						height: computedHeight,
						maxHeight: computedMaxHeight,
						position: 'relative' as const,
						...style,
					};
				})()}
				{...props}
			>
				<div
					ref={containerRef}
					className="flex-1 min-h-0 overflow-auto overscroll-y-contain custom-scrollbar"
					onScroll={shouldShowContent ? handleScroll : undefined}
				>
					{tableElement}
				</div>
				<div className={cn('border-t', colors.classes.borderDefault)}>
					<TablePaginationControls
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={goToPage}
						startIndex={pageStartIndex}
						endIndex={pageEndIndex}
						totalItems={totalItems}
						testIdPrefix={listKey ?? undefined}
					/>
				</div>
			</div>
		);
	}

	// Non-paginated: original single-div layout
	return (
		<div
			ref={containerRef}
			className={cn(
				'border rounded-t-lg h-full overflow-auto overscroll-y-contain custom-scrollbar',
				colors.classes.borderDefault,
				colors.classes.bgPrimary,
				className,
			)}
			style={(() => {
				const computedHeight = height ? height : undefined;
				const computedMaxHeight = shouldShowContent
					? !height && maxHeight
						? maxHeight
						: undefined
					: undefined;

				return {
					height: computedHeight,
					maxHeight: computedMaxHeight,
					position: 'relative' as const,
					...style,
				};
			})()}
			onScroll={shouldShowContent ? handleScroll : undefined}
			{...props}
		>
			{tableElement}
		</div>
	);
}
